// src/api/client.ts
import axios from 'axios';
import { config } from '../utils/config';

// Force HTTPS for any API URL in production
const getSecureApiUrl = () => {
  let baseUrl = config.API_URL;

  if (typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      baseUrl.startsWith('http:')) {
    baseUrl = baseUrl.replace('http:', 'https:');
  }

  return baseUrl;
};

// Create axios instance with secure base URL
export const apiClient = axios.create({
    baseURL: getSecureApiUrl(),
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 60000 // 60 second timeout for API calls
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
    (config) => {
        // Add auth token if available
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        // Handle specific error cases
        if (error.response) {
            // Handle unauthorized
            if (error.response.status === 401) {
                localStorage.removeItem('token');
                window.location.href = '/login';
                return Promise.reject(new Error('Authentication required. Please log in again.'));
            }

            // Handle resource not found
            if (error.response.status === 404) {
                return Promise.reject(new Error('The requested resource was not found.'));
            }

            // Handle server errors
            if (error.response.status >= 500) {
                return Promise.reject(new Error('Server error. Please try again later.'));
            }

            // Return the server's error message if available
            if (error.response.data?.detail) {
                return Promise.reject(new Error(error.response.data.detail));
            }
        } else if (error.request) {
            // The request was made but no response was received
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                return Promise.reject(new Error('Request timed out. The server may be busy. Please try again.'));
            }
            return Promise.reject(new Error('Unable to connect to server. Please check your internet connection.'));
        } else if (error.message === 'Network Error') {
            return Promise.reject(new Error('Network error. Please check your connection and try again.'));
        }

        return Promise.reject(error);
    }
);

export default apiClient;